import React, { useState, useEffect, useCallback } from "react";
import { linkWhatsApp } from "../api";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  Info,
  Search,
  X,
  XCircle,
} from "lucide-react";
import { brl, pct } from "./formato";

/* Placa Mercosul — identificador universal do veículo no sistema */
/* ══════════════════════════════════════════════════════════════════
   DOCUMENTOS PARA IMPRIMIR

   Uma folha só, escondida na tela e visível na impressão. O diálogo do
   navegador oferece "Salvar como PDF", então o mesmo botão serve para
   entregar no balcão e para mandar por mensagem.

   O cabeçalho vem do cadastro da oficina, não de constante: é o documento
   que o cliente leva embora, e sair com o nome errado é o pior lugar
   possível para esse erro.
   ══════════════════════════════════════════════════════════════════ */
function CabecalhoFolha({ of, titulo, numero, data }) {
  const endereco = [of?.logradouro, of?.numero, of?.bairro].filter(Boolean).join(", ");
  const cidade = [of?.municipio, of?.uf].filter(Boolean).join(" / ");
  const doc = (of?.cnpj || "").replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
  return (
    <div className="fh">
      <div>
        <div className="fh-nome">{of?.nome_fantasia || of?.razao_social || "Oficina"}</div>
        <div className="fh-dados">
          {of?.nome_fantasia && of?.razao_social ? <>{of.razao_social}<br /></> : null}
          {doc && <>CNPJ {doc}{of?.inscricao_estadual ? ` · IE ${of.inscricao_estadual}` : ""}<br /></>}
          {endereco && <>{endereco}<br /></>}
          {cidade}{of?.cep ? ` · CEP ${of.cep}` : ""}
          {of?.telefone && <><br />{of.telefone}</>}
          {of?.email && <> · {of.email}</>}
        </div>
      </div>
      <div className="fh-doc">
        <div className="t">{titulo}</div>
        <div className="n">{numero}</div>
        <div className="fh-dados">{data}</div>
      </div>
    </div>
  );
}

/** Itens com quantidade, unitário e total. Serve para OS e orçamento. */
function ItensFolha({ itens, desconto = 0 }) {
  const linha = (i) => (i.qtd || 0) * (i.valor || 0);
  const soma = itens.reduce((a, i) => a + linha(i), 0);
  const rotulo = { peca: "Peça", servico: "Serviço", terceirizado: "Terceirizado", taxa: "Taxa" };
  return (
    <>
      <h2>Itens</h2>
      <table>
        <thead><tr>
          <th style={{ width: "13%" }}>Tipo</th><th>Descrição</th>
          <th className="n" style={{ width: "9%" }}>Qtd</th>
          <th className="n" style={{ width: "16%" }}>Unitário</th>
          <th className="n" style={{ width: "17%" }}>Total</th>
        </tr></thead>
        <tbody>
          {itens.map((i) => (
            <tr key={i.id}>
              <td>{rotulo[i.tipo] ?? i.tipo}</td>
              <td>
                {i.desc}
                {/* Na folha o cliente vê quem fez cada serviço — era o pedido
                    da oficina, e é o que uma OS impressa deveria provar. */}
                {i.mec && <div style={{ fontSize: "8.5pt", color: "#555" }}>Executado por {i.mec}</div>}
              </td>
              <td className="n">{i.qtd}</td>
              <td className="n">{brl(i.valor)}</td>
              <td className="n">{brl(linha(i))}</td>
            </tr>
          ))}
          {itens.length === 0 && <tr><td colSpan={5}>Nenhum item lançado.</td></tr>}
        </tbody>
      </table>
      <div className="tot">
        <div><span>Subtotal</span><span>{brl(soma)}</span></div>
        {desconto > 0 && <div><span>Desconto</span><span>− {brl(desconto)}</span></div>}
        <div className="g"><span>Total</span><span>{brl(soma - desconto)}</span></div>
      </div>
    </>
  );
}

function Placa({ n, size = "sm" }) {
  const f = (n || "").replace(/[^A-Z0-9]/gi, "").toUpperCase();
  return (
    <span className={`plate plate-${size}`} title={`Placa ${f}`}>
      <span className="plate-top">BRASIL</span>
      <span className="plate-num">{f}</span>
    </span>
  );
}

function Btn({ variant = "o", size = undefined, icon: Icon, children, className = "", ...p }) {
  return (
    <button className={`btn btn-${variant} ${size ? "btn-" + size : ""} ${className}`} {...p}>
      {Icon && <Icon size={size === "xs" ? 13 : 15} strokeWidth={2.2} />}
      {children}
    </button>
  );
}

function Pill({ t = "mut", children, icon: Icon }) {
  return <span className={`pill p-${t}`}>{Icon && <Icon size={11} strokeWidth={2.5} />}{children}</span>;
}

/**
 * Abre a conversa no WhatsApp com a mensagem pronta.
 *
 * `linkWhatsApp` existia em api.ts desde sempre e não era usado em lugar
 * nenhum: todo botão de WhatsApp do app só mostrava um aviso dizendo que a
 * mensagem tinha sido enviada. Nada saía. O pior era o "Enviar ao cliente
 * pelo WhatsApp" do orçamento, que ainda mudava a OS para "aguardando" — a
 * oficina ficava esperando resposta de uma mensagem que nunca existiu.
 *
 * `wa.me` abre o WhatsApp com o texto digitado; quem aperta enviar é a
 * pessoa. É o máximo que dá para fazer do navegador, e é honesto: a tela não
 * afirma que enviou, ela abre a conversa.
 */
function abrirWhatsApp(telefone, mensagem, toast) {
  const tel = String(telefone ?? "").replace(/\D/g, "");
  if (!tel) {
    toast?.("Sem telefone", "Este cliente não tem telefone cadastrado.", "erro");
    return false;
  }
  window.open(linkWhatsApp(tel, mensagem), "_blank", "noopener");
  return true;
}

/** Copia para a área de transferência e avisa se o navegador recusar. */
async function copiar(texto, toast, oque = "Texto") {
  try {
    await navigator.clipboard.writeText(texto);
    toast?.(`${oque} copiado`, "Cole onde precisar.");
  } catch {
    toast?.("Não deu para copiar", "O navegador bloqueou. Selecione e copie à mão.", "erro");
  }
}

function Dado({ r, v, hint, tone }) {
  return (
    <div>
      <div className="lbl">{r}</div>
      <div className="text-sm font-semibold mono"
        style={tone ? { color: `var(--${tone})` } : undefined}>{v}</div>
      {hint && <div className="text-xs t-mut2 mt-0.5">{hint}</div>}
    </div>
  );
}

function Field({ label, children, hint, className = "" }) {
  return (
    <div className={className}>
      <label className="lbl">{label}</label>
      {children}
      {hint && <div className="text-xs t-mut2 mt-1">{hint}</div>}
    </div>
  );
}

function Modal({ open, onClose, title, sub, children, footer, w = "max-w-2xl" }) {
  useEffect(() => {
    if (!open) return;
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="ovl" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`mdl ${w}`} style={{ marginTop: 32, marginBottom: 32 }}>
        <div className="stripe" />
        <div className="flex items-start justify-between gap-4 px-5 py-4" style={{ borderBottom: "1px solid var(--line)" }}>
          <div>
            <h3 className="dsp" style={{ fontSize: 19 }}>{title}</h3>
            {sub && <div className="text-sm t-mut mt-0.5">{sub}</div>}
          </div>
          <button className="btn btn-g btn-xs" onClick={onClose} aria-label="Fechar"><X size={17} /></button>
        </div>
        <div className="px-5 py-4 scroll" style={{ maxHeight: "68vh", overflowY: "auto" }}>{children}</div>
        {footer && <div className="px-5 py-3 flex flex-wrap items-center justify-end gap-2" style={{ borderTop: "1px solid var(--line)", background: "var(--el)", borderRadius: "0 0 8px 8px" }}>{footer}</div>}
      </div>
    </div>
  );
}

function Toasts({ list, drop }) {
  return (
    <div className="fixed z-[90] flex flex-col gap-2" style={{ right: 18, bottom: 18, width: 330 }}>
      {list.map((t) => (
        <div key={t.id} className="card rise flex items-start gap-3 px-3.5 py-3" style={{ boxShadow: "0 8px 26px rgba(0,0,0,.16)", borderLeft: `3px solid var(--${t.tone === "erro" ? "crit" : t.tone === "alerta" ? "signal" : "ok"})` }}>
          {t.tone === "erro" ? <XCircle size={17} className="t-crit mt-0.5" /> : t.tone === "alerta" ? <AlertTriangle size={17} className="t-signal mt-0.5" /> : <CheckCircle2 size={17} className="t-ok mt-0.5" />}
          <div className="flex-1">
            <div className="text-sm font-semibold leading-tight">{t.titulo}</div>
            {t.msg && <div className="text-xs t-mut mt-0.5 leading-snug">{t.msg}</div>}
          </div>
          <button className="btn btn-g btn-xs" onClick={() => drop(t.id)}><X size={13} /></button>
        </div>
      ))}
    </div>
  );
}

function Stat({ label, value, sub, tone = "ink", icon: Icon, delta }) {
  return (
    <div className="card p-4 fade">
      <div className="flex items-start justify-between">
        <span className="eyebrow">{label}</span>
        {Icon && <Icon size={16} className={`t-${tone === "ink" ? "mut2" : tone}`} strokeWidth={2.2} />}
      </div>
      <div className="dsp mono mt-2" style={{ fontSize: 27, letterSpacing: "-.01em", color: tone === "ink" ? "var(--ink)" : `var(--${tone})` }}>{value}</div>
      <div className="flex items-center gap-2 mt-1">
        {delta != null && (
          <span className={`text-xs font-semibold flex items-center gap-0.5 ${delta >= 0 ? "t-ok" : "t-crit"}`}>
            {delta >= 0 ? <ArrowUp size={11} /> : <ArrowDown size={11} />}{Math.abs(delta)}%
          </span>
        )}
        {sub && <span className="text-xs t-mut">{sub}</span>}
      </div>
    </div>
  );
}

function Vazio({ icon: Icon = Info, titulo, msg, acao }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-14 px-6">
      <div className="flex items-center justify-center mb-3" style={{ width: 46, height: 46, borderRadius: 8, background: "var(--el)", border: "1px solid var(--line)" }}>
        <Icon size={21} className="t-mut2" />
      </div>
      <div className="dsp" style={{ fontSize: 16 }}>{titulo}</div>
      <div className="text-sm t-mut mt-1 max-w-sm">{msg}</div>
      {acao && <div className="mt-4">{acao}</div>}
    </div>
  );
}

function Busca({ v, set, ph = "Buscar…" }) {
  return (
    <div className="relative flex-1" style={{ minWidth: 180 }}>
      <Search size={15} className="absolute t-mut2" style={{ left: 10, top: "50%", transform: "translateY(-50%)" }} />
      <input className="inp" style={{ paddingLeft: 32 }} value={v} onChange={(e) => set(e.target.value)} placeholder={ph} />
    </div>
  );
}

function Barra({ v, max, tone }) {
  const pct = Math.min(100, (v / (max || 1)) * 100);
  return <div className="bar" style={{ width: 62 }}><i style={{ width: `${pct}%`, background: `var(--${tone})` }} /></div>;
}

/* ══════════════════════════════════════════════════════════════════
   PONTE COM O SERVIDOR
   As telas nasceram gravando em memória. Agora escrevem de verdade —
   e o que muda não é só persistir: o servidor RECUSA o que não pode.
   Aprovar OS sem peça em estoque falha aqui, e a tela precisa mostrar.
   ══════════════════════════════════════════════════════════════════ */
/* eslint-disable react-hooks/rules-of-hooks --
   `usarServidor` É um hook — "usar" é "use" em português, e o código do
   projeto é em português por decisão. A regra reconhece hook pelo prefixo
   `use`, então não enxerga este. Desligado só aqui; continua valendo em
   todo o resto do arquivo. */
function usarServidor(toast) {
  const [ocupado, setOcupado] = useState(false);
  const executar = useCallback(async (fn, aoDarCerto, rotulo) => {
    setOcupado(true);
    try {
      const r = await fn();
      aoDarCerto?.(r);
      return { ok: true, dados: r };
    } catch (e) {
      // A mensagem já vem pronta do domínio, em português e acionável:
      // "Não dá para aprovar sem essas peças — Pastilha: disponível 1,
      // precisa 2". Trocar por texto genérico seria jogar isso fora.
      toast(rotulo ?? "Não deu certo",
        e?.message ?? "Sem conexão com o servidor. Verifique a rede.", "erro");
      return { ok: false, erro: e };
    } finally {
      setOcupado(false);
    }
  }, [toast]);
  return { executar, ocupado };
}
/* eslint-enable react-hooks/rules-of-hooks */

export { CabecalhoFolha, ItensFolha, Placa, Btn, Pill, Dado, Field, Modal, Toasts, Stat, Vazio, Busca, Barra, usarServidor, abrirWhatsApp, copiar };
